import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Column } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { Page } from '@unistocks/components/page/page';
import { VirtualList } from '@unistocks/components/virtual-list/list';
import { SavingsGoalHistory } from '@unistocks/features/savings-goals/components/cards/savings-goal-history';
import { SavingsGoalSettings } from '@unistocks/features/savings-goals/components/cards/savings-goal-settings';
import { SavingsGoalStatus } from '@unistocks/features/savings-goals/components/cards/savings-goal-status';
import { SavingsGoalsHeaderRight } from '@unistocks/features/savings-goals/components/header/header-right';
import { SavingsGoalHeaderTitle } from '@unistocks/features/savings-goals/components/header/header-title';
import { SavingsGoalProgressBar } from '@unistocks/features/savings-goals/components/savings-goal-progress-bar';
import {
  savingsGoalsGetQueryKey,
  savingsGoalsGetSavingsGoalProgressQueryKey,
  useSavingsGoalsGetQuery,
  useSavingsGoalsGetSavingsGoalProgressQuery
} from '@unistocks/proxy/axios-client/SavingsGoalsQuery';
import { useDelayedRender } from '@unistocks/shared/hooks/use-delayed-value';
import { t } from '@unistocks/shared/localization/index.ts';
import { HasId } from '@unistocks/types/has-id';
import React from 'react';
import { useMedia } from 'tamagui';

const SavingsGoalChart = React.lazy(
  () => import('@unistocks/features/savings-goals/components/chart/savings-goal-chart')
);

const cardItems = ['status', 'history', 'settings'] as const;

export const SavingsGoal: React.FC<HasId> = ({ id }) => {
  // This will significantly speed up the navigation to the savings goal, and render the chart component after the delay
  const delayedReady = useDelayedRender(0);
  const media = useMedia();
  const numColumns = media.xxl ? 3 : media.lg ? 2 : 1;

  const {
    data: savingsGoal,
    isSuccess: isSuccessSavingsGoal,
    isRefetching: isRefetchingSavingsGoal,
    refetch: refetchSavingsGoal
  } = useSavingsGoalsGetQuery({ id });

  const {
    data: savingsGoalProgress,
    isSuccess: isSuccessProgress,
    isRefetching: isRefetchingProgress,
    refetch: refetchProgress
  } = useSavingsGoalsGetSavingsGoalProgressQuery({ id });

  const refetch = () => {
    refetchSavingsGoal();
    refetchProgress();
  };

  return (
    <Page>
      <Page.ScrollView
        refreshing={isRefetchingSavingsGoal || isRefetchingProgress}
        onRefresh={refetch}
        headerBackArrow={false}
        headerTitle={
          <SavingsGoalHeaderTitle>
            {isSuccessSavingsGoal ? savingsGoal.name : null}
          </SavingsGoalHeaderTitle>
        }
        headerIcon={<SavingsGoalsHeaderRight id={id} />}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
        queryState={{
          queryKeys: [savingsGoalsGetQueryKey(id), savingsGoalsGetSavingsGoalProgressQueryKey(id)],
          loadingText: t('::SavingsGoal:Loading')
        }}
      >
        {isSuccessSavingsGoal && isSuccessProgress && delayedReady ? (
          <Column gap={'$3'}>
            <React.Suspense fallback={<Loader pt={'$3'} />}>
              <Page.Content px={0} $sm={{ px: '$3' }}>
                <SavingsGoalChart
                  savingsGoal={savingsGoal}
                  goalCalculation={savingsGoalProgress}
                  chartContainerProps={{ h: 400, $md: { h: 500 }, $xl: { h: 600 } }}
                />
              </Page.Content>
            </React.Suspense>
            <SavingsGoalProgressBar
              savingsGoal={savingsGoal}
              goalCalculation={savingsGoalProgress}
              transition={'quicker'}
              mx={'auto'}
              w={'95%'}
              $md={{ w: '75%' }}
              $lg={{ w: '65%' }}
              $xl={{ w: '50%' }}
            />
            <VirtualList
              masonry={numColumns > 1}
              numColumns={numColumns}
              optimizeItemArrangement={false}
              scrollEnabled={false}
              data={cardItems}
              renderItem={({ item }) => {
                switch (item) {
                  case 'status':
                    return (
                      <SavingsGoalStatus
                        savingsGoal={savingsGoal}
                        goalCalculation={savingsGoalProgress}
                        m={'$2'}
                      />
                    );

                  case 'history':
                    return (
                      <SavingsGoalHistory
                        savingsGoal={savingsGoal}
                        goalCalculation={savingsGoalProgress}
                        m={'$2'}
                      />
                    );

                  case 'settings':
                    return (
                      <SavingsGoalSettings
                        savingsGoal={savingsGoal}
                        goalCalculation={savingsGoalProgress}
                        m={'$2'}
                      />
                    );
                }
              }}
            />
          </Column>
        ) : null}
      </Page.ScrollView>
    </Page>
  );
};

SavingsGoal.displayName = 'SavingsGoal';
